import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import {
  Add,
  Block,
  ContentCopy,
  Delete,
  EventAvailable,
  ExpandMore,
  Link as LinkIcon,
  PictureAsPdf,
  Settings,
} from "@mui/icons-material";
import dayjs, { Dayjs } from "dayjs";
import {
  cancelVenueBooking,
  deleteVenueAvailability,
  deleteVenueBlock,
  fetchVenueAvailability,
  fetchVenueBlocks,
  fetchVenueBookings,
  setVenueHireShare,
} from "./api";
import type {
  Club,
  VenueAvailabilityWindow,
  VenueBlock,
  VenueBooking,
} from "./types";
import {
  formatAvailabilityPrices,
  formatDate,
  formatSlotRange,
  formatTime,
  firstDateWithItems,
  getRollingDateRange,
  groupByDate,
  normalizeAvailabilityWindow,
  normalizeVenueHireSettings,
  toDateString,
} from "./venueHireUtils";
import VenueHireDateCalendar, {
  datesToHighlightMap,
  defaultShouldDisableUnlessHighlighted,
} from "./VenueHireDateCalendar";

const VENUE_HIRE_GUIDE_URL =
  "https://s3.eu-west-2.amazonaws.com/easyagility.co.uk/venue-hire-admin-guide.pdf";

interface VenueHireSectionProps {
  club: Club;
  expanded: boolean;
  onExpandChange: (expanded: boolean) => void;
  onOpenSettings: (club: Club) => void;
  onAddAvailability: (club: Club) => void;
  onBlockTime: (club: Club) => void;
  onRefreshClub: () => Promise<void> | void;
  onError: (message: string) => void;
  refreshKey?: number;
}

const VenueHireSection: React.FC<VenueHireSectionProps> = ({
  club,
  expanded,
  onExpandChange,
  onOpenSettings,
  onAddAvailability,
  onBlockTime,
  onRefreshClub,
  onError,
  refreshKey = 0,
}) => {
  const theme = useTheme();
  const settings = club.venue_hire;
  const enabled = settings?.enabled ?? false;

  const [loading, setLoading] = useState(false);
  const [availability, setAvailability] = useState<VenueAvailabilityWindow[]>(
    [],
  );
  const [blocks, setBlocks] = useState<VenueBlock[]>([]);
  const [bookings, setBookings] = useState<VenueBooking[]>([]);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [shareLoading, setShareLoading] = useState(false);
  const [copiedShareLink, setCopiedShareLink] = useState(false);
  const [selectedAvailabilityDate, setSelectedAvailabilityDate] =
    useState<Dayjs | null>(null);
  const [rangeEnd, setRangeEnd] = useState<Dayjs>(() =>
    dayjs(getRollingDateRange(8).to),
  );

  const loadData = useCallback(async () => {
    if (!enabled) return;
    try {
      setLoading(true);
      const range = getRollingDateRange(8);
      setRangeEnd(dayjs(range.to));
      const [availabilityData, blocksData, bookingsData] = await Promise.all([
        fetchVenueAvailability(club.id, range.from, range.to),
        fetchVenueBlocks(club.id, range.from, range.to),
        fetchVenueBookings(club.id),
      ]);
      setAvailability(availabilityData);
      setBlocks(blocksData);
      setBookings(bookingsData);
    } catch (err) {
      console.error("Error loading venue hire data:", err);
      onError("Failed to load venue hire data.");
    } finally {
      setLoading(false);
    }
  }, [club.id, enabled, onError]);

  useEffect(() => {
    if (expanded && enabled) {
      loadData();
    }
  }, [expanded, enabled, loadData, refreshKey]);

  const availabilityByDate = groupByDate(availability);
  const blocksByDate = groupByDate(blocks);

  const availabilityHighlightDates = useMemo(
    () => datesToHighlightMap(availability.map((window) => window.date)),
    [availability],
  );

  useEffect(() => {
    if (loading || availability.length === 0) {
      if (!loading && availability.length === 0) {
        setSelectedAvailabilityDate(null);
      }
      return;
    }
    setSelectedAvailabilityDate((current) => {
      if (
        current &&
        availabilityHighlightDates[toDateString(current)]
      ) {
        return current;
      }
      const next = firstDateWithItems(availability);
      return next ? dayjs(next) : null;
    });
  }, [availability, loading, availabilityHighlightDates]);

  const selectedAvailabilityDateStr = selectedAvailabilityDate
    ? toDateString(selectedAvailabilityDate)
    : null;
  const selectedDayWindows = selectedAvailabilityDateStr
    ? availabilityByDate[selectedAvailabilityDateStr] ?? []
    : [];

  const handleDeleteAvailability = async (windowId: number) => {
    try {
      setDeletingId(`window-${windowId}`);
      await deleteVenueAvailability(windowId);
      await loadData();
      await onRefreshClub();
    } catch (err) {
      console.error("Error deleting availability:", err);
      onError("Failed to delete availability window.");
    } finally {
      setDeletingId(null);
    }
  };

  const handleDeleteBlock = async (blockId: number) => {
    try {
      setDeletingId(`block-${blockId}`);
      await deleteVenueBlock(blockId);
      await loadData();
    } catch (err) {
      console.error("Error deleting block:", err);
      onError("Failed to delete blocked time.");
    } finally {
      setDeletingId(null);
    }
  };

  const handleCancelBooking = async (bookingId: number) => {
    try {
      setDeletingId(`booking-${bookingId}`);
      await cancelVenueBooking(bookingId);
      await loadData();
    } catch (err) {
      console.error("Error cancelling booking:", err);
      onError("Failed to cancel booking.");
    } finally {
      setDeletingId(null);
    }
  };

  const shareUrl = club.venue_hire_share_token
    ? `${window.location.origin}/venue-hire/${club.venue_hire_share_token}`
    : null;

  const handleShareAction = async (
    action: "enable" | "regenerate" | "disable",
  ) => {
    try {
      setShareLoading(true);
      await setVenueHireShare(club.id, action);
      await onRefreshClub();
    } catch (err) {
      console.error("Error updating share link:", err);
      onError("Failed to update the booking share link.");
    } finally {
      setShareLoading(false);
    }
  };

  const handleCopyShareLink = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopiedShareLink(true);
      setTimeout(() => setCopiedShareLink(false), 2000);
    } catch (err) {
      console.error("Error copying share link:", err);
      onError("Failed to copy the booking link.");
    }
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Accordion
        expanded={expanded}
        onChange={(_, isExpanded) => onExpandChange(isExpanded)}
      >
        <AccordionSummary
          expandIcon={<ExpandMore />}
          sx={{
            bgcolor: theme.palette.info.light + "10",
            borderRadius: 1,
            mb: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              gap: 1,
              pr: { xs: 0.5, sm: 2 },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <EventAvailable sx={{ flexShrink: 0 }} />
              <Typography sx={{ fontWeight: 600 }}>Venue Hire</Typography>
            </Box>
            <Chip
              label={enabled ? "Enabled" : "Disabled"}
              size="small"
              color={enabled ? "success" : "default"}
              sx={{ flexShrink: 0 }}
            />
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ px: { xs: 1, sm: 2 } }}>
          <Stack
            direction="row"
            spacing={1}
            useFlexGap
            flexWrap="wrap"
            sx={{ mb: 2 }}
          >
            <Button
              variant="outlined"
              size="small"
              startIcon={<Settings />}
              onClick={() => onOpenSettings(club)}
            >
              Settings
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Add />}
              disabled={!enabled}
              onClick={() => onAddAvailability(club)}
            >
              Add Availability
            </Button>
            <Button
              variant="outlined"
              size="small"
              color="warning"
              startIcon={<Block />}
              disabled={!enabled}
              onClick={() => onBlockTime(club)}
            >
              Block Time
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<PictureAsPdf />}
              component="a"
              href={VENUE_HIRE_GUIDE_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              Venue Hire guide
            </Button>
          </Stack>

          {enabled && (
            <Box
              sx={{
                mb: 2,
                p: 2,
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
                bgcolor: "background.paper",
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                Booking link for non-members
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Share a link so people outside the club can sign in and book DIY
                slots. The link only works while sharing is enabled.
              </Typography>
              {shareUrl ? (
                <Stack spacing={1.5}>
                  <TextField
                    value={shareUrl}
                    size="small"
                    fullWidth
                    InputProps={{ readOnly: true }}
                  />
                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<ContentCopy />}
                      onClick={handleCopyShareLink}
                      disabled={shareLoading}
                    >
                      {copiedShareLink ? "Copied" : "Copy link"}
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => handleShareAction("regenerate")}
                      disabled={shareLoading}
                    >
                      Regenerate
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      color="warning"
                      onClick={() => handleShareAction("disable")}
                      disabled={shareLoading}
                    >
                      Stop sharing
                    </Button>
                  </Stack>
                </Stack>
              ) : (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<LinkIcon />}
                  onClick={() => handleShareAction("enable")}
                  disabled={shareLoading}
                >
                  Create share link
                </Button>
              )}
            </Box>
          )}

          {!enabled ? (
            <Alert severity="info">
              Venue hire is disabled. Enable it in settings to let members book
              DIY sessions.
            </Alert>
          ) : loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <>
              {settings?.notes && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {settings.notes}
                </Alert>
              )}
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Default prices:{" "}
                {formatAvailabilityPrices(normalizeVenueHireSettings(settings))}
              </Typography>

              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                Availability windows
              </Typography>
              {availability.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  No availability added yet.
                </Typography>
              ) : (
                <Grid container spacing={3} sx={{ mb: 2 }} alignItems="flex-start">
                  <Grid item xs={12} md={5} lg={4}>
                    <VenueHireDateCalendar
                      value={selectedAvailabilityDate}
                      onChange={setSelectedAvailabilityDate}
                      highlightedDates={availabilityHighlightDates}
                      minDate={dayjs()}
                      maxDate={rangeEnd}
                      shouldDisableDate={defaultShouldDisableUnlessHighlighted(
                        availabilityHighlightDates,
                      )}
                      highlightColor="primary"
                      legendLabel="Availability set"
                      legendCount={
                        Object.keys(availabilityByDate).length
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={7} lg={8}>
                    {selectedAvailabilityDateStr ? (
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 700, mb: 1.5 }}
                        >
                          {formatDate(selectedAvailabilityDateStr)}
                        </Typography>
                        {selectedDayWindows.length === 0 ? (
                          <Typography variant="body2" color="text.secondary">
                            No windows on this date.
                          </Typography>
                        ) : (
                          <List dense disablePadding>
                            {selectedDayWindows.map((window) => (
                              <ListItem
                                key={window.id}
                                secondaryAction={
                                  <IconButton
                                    edge="end"
                                    size="small"
                                    disabled={
                                      deletingId === `window-${window.id}`
                                    }
                                    onClick={() =>
                                      handleDeleteAvailability(window.id)
                                    }
                                  >
                                    <Delete fontSize="small" />
                                  </IconButton>
                                }
                                sx={{
                                  border: 1,
                                  borderColor: "divider",
                                  borderRadius: 1,
                                  mb: 0.5,
                                }}
                              >
                                <ListItemText
                                  primary={`${formatTime(window.start_time)} – ${formatTime(window.end_time)}`}
                                  secondary={formatAvailabilityPrices(
                                    normalizeAvailabilityWindow(window),
                                  )}
                                />
                              </ListItem>
                            ))}
                          </List>
                        )}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Select a highlighted date to view availability windows.
                      </Typography>
                    )}
                  </Grid>
                </Grid>
              )}

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                Blocked times
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Scheduled training sessions are automatically blocked for 2 hours
                from each session start, plus 30 minutes either side.
              </Typography>
              {blocks.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  No blocked times.
                </Typography>
              ) : (
                Object.entries(blocksByDate).map(([date, dateBlocks]) => (
                  <Box key={date} sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                      {formatDate(date)}
                    </Typography>
                    <List dense disablePadding>
                      {dateBlocks.map((block) => {
                        const isAutoBlock = block.id === 0;
                        const blockKey = isAutoBlock
                          ? `auto-${block.date}-${block.start_time}`
                          : `block-${block.id}`;
                        return (
                        <ListItem
                          key={blockKey}
                          secondaryAction={
                            isAutoBlock ? undefined : (
                            <IconButton
                              edge="end"
                              size="small"
                              disabled={deletingId === `block-${block.id}`}
                              onClick={() => handleDeleteBlock(block.id)}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                            )
                          }
                          sx={{
                            border: 1,
                            borderColor: isAutoBlock ? "info.light" : "warning.light",
                            borderRadius: 1,
                            mb: 0.5,
                          }}
                        >
                          <ListItemText
                            primary={`${formatTime(block.start_time)} – ${formatTime(block.end_time)}`}
                            secondary={
                              isAutoBlock
                                ? `${block.reason || "Training session"} · Automatic`
                                : block.reason || "Blocked"
                            }
                          />
                        </ListItem>
                        );
                      })}
                    </List>
                  </Box>
                ))
              )}

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                Bookings
              </Typography>
              {bookings.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No bookings yet.
                </Typography>
              ) : (
                <>
                  {bookings.some((b) => !b.paid) && (
                    <Alert severity="info" sx={{ mb: 1.5 }}>
                      Cancelling an unpaid booking releases the slot. Any refunds
                      for paid bookings are handled manually by the club.
                    </Alert>
                  )}
                <List dense disablePadding>
                  {bookings.map((booking) => (
                    <ListItem
                      key={booking.id}
                      secondaryAction={
                        !booking.paid ? (
                          <Button
                            size="small"
                            color="warning"
                            disabled={deletingId === `booking-${booking.id}`}
                            onClick={() => handleCancelBooking(booking.id)}
                          >
                            Cancel
                          </Button>
                        ) : undefined
                      }
                      sx={{
                        border: 1,
                        borderColor: "divider",
                        borderRadius: 1,
                        mb: 0.5,
                      }}
                    >
                      <ListItemText
                        primary={formatSlotRange(
                          booking.date,
                          booking.start_time,
                          booking.end_time,
                        )}
                        secondary={`${booking.handler_name} · £${booking.price.toFixed(2)} · ${booking.paid ? "Paid" : "Awaiting payment"}`}
                      />
                    </ListItem>
                  ))}
                </List>
                </>
              )}
            </>
          )}
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default VenueHireSection;
